import type { HealthPoint } from '../../../data/ServerApiClient'

// Pure presentation helpers for the composite financial-health score. The score is computed
// server-side (ratio_health.rs) and served; the client only maps a 0-100 score to a letter grade,
// tone colors, and the "grade change" markers on the trend. The +/- bands MUST match
// ratio_health::grade_for exactly (parity = 82 = B). Dependency-free so it unit-tests cleanly.

/** Letter grade (A+ … F) for a 0..100 score — mirrors ratio_health::grade_for. */
export function scoreToGrade(score: number): string {
  if (score >= 98) return 'A+'
  if (score >= 94) return 'A'
  if (score >= 90) return 'A-'
  if (score >= 86) return 'B+'
  if (score >= 82) return 'B'
  if (score >= 78) return 'B-'
  if (score >= 74) return 'C+'
  if (score >= 70) return 'C'
  if (score >= 66) return 'C-'
  if (score >= 63) return 'D+'
  if (score >= 59) return 'D'
  if (score >= 55) return 'D-'
  return 'F'
}

export type GradeChange = {
  period: string
  score: number
  direction: 'up' | 'down'
  grade: string
}

/** Months where the letter grade changed vs the prior point — the trend's "moved a grade" markers. */
export function gradeChangePoints(series: HealthPoint[]): GradeChange[] {
  const out: GradeChange[] = []
  for (let i = 1; i < series.length; i++) {
    const prev = scoreToGrade(series[i - 1].score)
    const cur = scoreToGrade(series[i].score)
    if (cur !== prev) {
      out.push({
        period: series[i].period,
        score: series[i].score,
        direction: series[i].score >= series[i - 1].score ? 'up' : 'down',
        grade: cur,
      })
    }
  }
  return out
}

export type HealthTone = 'good' | 'watch' | 'bad'

export function scoreTone(score: number): HealthTone {
  if (score >= 78) return 'good'
  if (score >= 66) return 'watch'
  return 'bad'
}

/** Tone colors for a health score, drawn from the same CFO tokens as the ratio assessment tones. */
export function healthToneColors(score: number): { color: string; bg: string; edge: string } {
  switch (scoreTone(score)) {
    case 'good':
      return { color: 'var(--good-acc)', bg: 'var(--good-bg)', edge: 'var(--good-acc)' }
    case 'watch':
      return { color: 'var(--check-acc)', bg: 'var(--check-bg)', edge: 'var(--check-acc)' }
    default:
      return { color: 'var(--neg)', bg: 'var(--neg-soft)', edge: 'var(--neg)' }
  }
}

/** Higher is always better for the composite, so a positive month-over-month delta is good (teal). */
export function deltaColor(delta: number | null | undefined): string {
  if (delta == null) return 'var(--muted)'
  if (delta > 0.5) return 'var(--good-acc)'
  if (delta < -0.5) return 'var(--neg)'
  return 'var(--muted)'
}

/** "+4" / "−3" / "0" — a signed, rounded delta label. */
export function formatDelta(delta: number | null | undefined): string {
  if (delta == null) return '—'
  const r = Math.round(delta)
  if (r > 0) return `+${r}`
  if (r < 0) return `−${Math.abs(r)}`
  return '0'
}

/** Round a 0-100 score for display. */
export const formatScore = (score: number | null | undefined): string =>
  score == null ? '—' : String(Math.round(score))
